import { useCallback, useRef } from 'react';
import { DefaultErrorHandlersProvider } from '@antimatterhq/ui/contexts/DefaultErrorHandlerContext';
import { DefaultSuccessHandlersProvider } from '@antimatterhq/ui/contexts/DefaultSuccessHandlerContext';
import { AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { isNetworkError } from '@antimatterhq/ui/utils/isNetworkError';
import { networkStatusAtom } from '@/atoms/networkStatus/networkStatusAtoms';
import { useClientAuthentication } from '@/hooks/authentication/useClientAuthentication';
import { useFrameState } from '@/providers/frame-state';
import { sendMessageToParentFrame } from '@/utils/sendMessageToParentFrame';

const useClientDefaultHandlers = () => {
  const { authHandledByParent } = useFrameState();
  const { refreshAuthentication } = useClientAuthentication();

  // Debounce the transition back to "online" to avoid toast flicker.
  // Wait a little longer before declaring the connection “restored” to ensure
  // flakier links don’t cause toast-flicker.  3 seconds feels like a good
  // compromise between responsiveness and stability.
  const DEFAULT_ONLINE_DEBOUNCE_MS = 1500;

  const onlineTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const setNetworkStatusOffline = useAtomCallback(
    useCallback((_, set) => {
      set(networkStatusAtom, 'offline');
    }, []),
  );

  const setNetworkStatusOnline = useAtomCallback(
    useCallback((_, set) => {
      set(networkStatusAtom, 'online');
    }, []),
  );

  const defaultAxiosErrorHandler = useCallback(
    async (error: AxiosError) => {
      if (onlineTimeoutRef.current) {
        clearTimeout(onlineTimeoutRef.current);
        onlineTimeoutRef.current = undefined;
      }
      // Handle 401 errors
      if (error.response?.status === 401) {
        if (authHandledByParent) {
          sendMessageToParentFrame({ error: 401 });
          return true;
        } else {
          await refreshAuthentication();
          return true;
        }
      }

      // Handle network errors
      if (isNetworkError(error)) {
        if (authHandledByParent) {
          sendMessageToParentFrame({ networkStatus: 'offline' });
        } else {
          setNetworkStatusOffline();
        }

        // Mark as unhandled so react-query's retry mechanism takes over.
        return false;
      }

      return false;
    },
    [authHandledByParent, refreshAuthentication, setNetworkStatusOffline],
  );

  const defaultSuccessHandler = useCallback(async () => {
    if (onlineTimeoutRef.current) clearTimeout(onlineTimeoutRef.current);

    onlineTimeoutRef.current = setTimeout(() => {
      if (authHandledByParent) {
        sendMessageToParentFrame({ networkStatus: 'online' });
      } else {
        setNetworkStatusOnline();
      }
      onlineTimeoutRef.current = undefined;
    }, DEFAULT_ONLINE_DEBOUNCE_MS);
  }, [authHandledByParent, setNetworkStatusOnline]);

  return { defaultAxiosErrorHandler, defaultSuccessHandler };
};

export const ClientRequestHandlersProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const { defaultAxiosErrorHandler, defaultSuccessHandler } =
    useClientDefaultHandlers();

  return (
    <DefaultErrorHandlersProvider
      defaultAxiosErrorHandler={defaultAxiosErrorHandler}
    >
      <DefaultSuccessHandlersProvider
        defaultSuccessHandler={defaultSuccessHandler}
      >
        {children}
      </DefaultSuccessHandlersProvider>
    </DefaultErrorHandlersProvider>
  );
};
